"use client";
import { useState, useEffect } from "react";
import Image from "next/image";

// Mock data (will be replaced with real API/CMS data later)
const COTTAGES = [
  {
    id: "cottage-standard",
    title: "Коттедж «Стандарт Комфорт»",
    image: "https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?auto=format&fit=crop&q=80&w=800",
    features: ["Двуспальная кровать", "Вид на летний бассейн", "Сплит-система", "Капсульная кофемашина"],
    size: "35 м²"
  },
  {
    id: "cottage-upgraded",
    title: "Коттедж «Стандарт Улучшенный»",
    image: "https://images.unsplash.com/photo-1542314831-c6a4d27ce66b?auto=format&fit=crop&q=80&w=800",
    features: ["Увеличенная площадь", "Зона отдыха с диваном", "Своя терраса", "Премиум-косметика"],
    size: "45 м²"
  }
];

const MAIN_BUILDING = [
  { id: "mb-standart-premium", title: "Стандарт Премиум", image: "https://images.unsplash.com/photo-1618773928121-c32242e63f39?auto=format&fit=crop&q=80&w=800", features: ["Двуспальная кровать", "Рабочая зона", "Премиум-косметика"], size: "28 м²" },
  { id: "mb-studio", title: "Студия", image: "https://images.unsplash.com/photo-1590490360182-c33d57733427?auto=format&fit=crop&q=80&w=800", features: ["Панорамное остекление", "Гостиная зона", "Кофемашина"], size: "40 м²" },
  { id: "mb-junior", title: "Джуниор Сюит", image: "https://images.unsplash.com/photo-1578683010236-d716f9a3f461?auto=format&fit=crop&q=80&w=800", features: ["Расширенная площадь", "Мягкая зона отдыха", "Тропический душ"], size: "45 м²" },
  { id: "mb-junior-premium", title: "Джуниор Сюит Премиум", image: "https://images.unsplash.com/photo-1566665797739-1674de7a421a?auto=format&fit=crop&q=80&w=800", features: ["King-size кровать", "Дизайнерский интерьер", "Меню подушек"], size: "50 м²" },
  { id: "mb-junior-comfort", title: "Джуниор Сюит Комфорт", image: "https://images.unsplash.com/photo-1631049307264-da0ec9d70304?auto=format&fit=crop&q=80&w=800", features: ["Улучшенная звукоизоляция", "Два смарт-ТВ", "Чайная станция"], size: "55 м²" },
  { id: "mb-suite", title: "Люкс", image: "https://images.unsplash.com/photo-1581404917879-53e19259f56e?auto=format&fit=crop&q=80&w=800", features: ["Две изолированные комнаты", "Джакузи", "VIP-косметика"], size: "70 м²" },
  { id: "mb-family", title: "Люкс Семейный", image: "https://images.unsplash.com/photo-1560067174-c5a3a8f37060?auto=format&fit=crop&q=80&w=800", features: ["Две спальни", "Два санузла", "Обеденная зона"], size: "95 м²" }
];

export default function Rooms() {
  const [activeTab, setActiveTab] = useState<"main" | "cottages">("main");
  const [selectedRoom, setSelectedRoom] = useState<typeof MAIN_BUILDING[0] | null>(null);

  const activeRooms = activeTab === "main" ? MAIN_BUILDING : COTTAGES;

  useEffect(() => {
    if (selectedRoom) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [selectedRoom]);

  return (
    <section id="rooms" className="py-32 bg-slate-900 relative">
      <div className="container mx-auto px-6 relative z-10">
        
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Искусство <span className="text-emerald-400">размещения</span>
          </h2>
          <p className="text-slate-400 text-lg font-light">
            Выберите формат своего отдыха. Главный корпус с прямым доступом к SPA в тапочках, или уединенные каменные эко-коттеджи с собственным бассейном.
          </p>
        </div>

        {/* Custom Segmented Control (Tabs) */}
        <div className="flex justify-center mb-16">
          <div className="glass-effect p-1.5 rounded-full inline-flex relative border-white/20">
            {/* Sliding highlight */}
            <div 
              className={`absolute top-1.5 bottom-1.5 w-[calc(50%-6px)] bg-emerald-500 rounded-full transition-all duration-300 ease-out shadow-lg ${
                activeTab === "main" ? "left-1.5" : "left-[calc(50%+4.5px)]"
              }`}
            ></div>
            
            <button
              onClick={() => setActiveTab("main")}
              className={`relative z-10 px-8 py-3 rounded-full text-sm font-semibold transition-colors duration-300 w-48 ${
                activeTab === "main" ? "text-white" : "text-slate-300 hover:text-white"
              }`}
            >
              Главный корпус
            </button>
            <button
              onClick={() => setActiveTab("cottages")}
              className={`relative z-10 px-8 py-3 rounded-full text-sm font-semibold transition-colors duration-300 w-48 ${
                activeTab === "cottages" ? "text-white" : "text-slate-300 hover:text-white"
              }`}
            >
              Эко-Коттеджи
            </button>
          </div>
        </div>

        {/* Room Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {activeRooms.map((room) => (
            <div 
              key={room.id}
              onClick={() => setSelectedRoom(room)}
              className="group glass-effect rounded-3xl overflow-hidden border border-white/10 hover:border-emerald-500/30 transition-all duration-500 hover:shadow-[0_10px_40px_-10px_rgba(16,185,129,0.3)] cursor-pointer"
            >
              {/* Image Container with hover zoom */}
              <div className="relative h-64 overflow-hidden bg-slate-800">
                <Image
                  src={room.image}
                  alt={room.title}
                  fill
                  className="object-cover group-hover:scale-110 transition-transform duration-700 ease-in-out"
                  sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                />
                {/* Size Badge */}
                <div className="absolute top-4 right-4 bg-black/60 backdrop-blur-md px-3 py-1 rounded-full text-xs font-semibold text-white border border-white/20">
                  {room.size}
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <h3 className="text-xl font-bold text-white mb-4 group-hover:text-emerald-400 transition-colors">
                  {room.title}
                </h3>
                <ul className="space-y-2 mb-8">
                  {room.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-3 text-sm text-slate-300">
                      <div className="w-1.5 h-1.5 rounded-full bg-emerald-500"></div>
                      {feature}
                    </li>
                  ))}
                </ul>
                
                <div className="flex items-center justify-between border-t border-white/10 pt-4">
                  <span className="text-emerald-400 font-medium text-sm">Подробнее о номере</span>
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5 text-slate-400 group-hover:text-emerald-400 transition-colors group-hover:translate-x-1 duration-300">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
                  </svg>
                </div>
              </div>
            </div>
          ))}
        </div>

      </div>

      {/* OVERLAY & SIDE SHEET (Drawer) */}
      <div 
        className={`fixed inset-0 z-40 bg-black/60 backdrop-blur-sm transition-opacity duration-300 ${
          selectedRoom ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        onClick={() => setSelectedRoom(null)}
      ></div>

      <div 
        className={`fixed inset-y-0 right-0 z-50 w-full max-w-xl bg-slate-900 border-l border-white/10 shadow-2xl transform transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] overflow-y-auto flex flex-col ${
          selectedRoom ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {selectedRoom && (
          <>
            {/* Drawer Header (Sticky Close Button) */}
            <div className="absolute top-6 right-6 z-20">
              <button 
                onClick={() => setSelectedRoom(null)}
                className="w-10 h-10 rounded-full bg-black/50 backdrop-blur-md border border-white/20 flex items-center justify-center text-white hover:bg-emerald-500 transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Huge Image Header */}
            <div className="relative h-80 w-full shrink-0">
              <Image 
                src={selectedRoom.image}
                alt={selectedRoom.title}
                fill
                className="object-cover"
                sizes="(max-width: 768px) 100vw, 600px"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900 to-transparent"></div>
              <div className="absolute bottom-6 left-8">
                <div className="bg-emerald-500/20 backdrop-blur-md border border-emerald-500/30 text-emerald-300 text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wider mb-3 w-fit">
                  {selectedRoom.size}
                </div>
                <h2 className="text-3xl font-bold text-white">{selectedRoom.title}</h2>
              </div>
            </div>

            {/* Drawer Content */}
            <div className="p-8 flex-1">
              <p className="text-slate-300 font-light leading-relaxed mb-8">
                Просторный номер премиум-класса с панорамными окнами и идеальным видом на природу. Дизайн интерьера выполнен с использованием экологичных материалов. Здесь продумана каждая деталь для вашего максимального комфорта и расслабления после SPA-процедур.
              </p>
              
              <h4 className="text-white font-semibold mb-4 text-lg">Что есть в номере:</h4>
              <div className="grid grid-cols-2 gap-4 mb-12">
                {selectedRoom.features.map((feature, idx) => (
                  <div key={idx} className="flex items-center gap-3 glass-effect p-3 rounded-xl border border-white/5">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 text-emerald-400 shrink-0">
                      <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm text-slate-300">{feature}</span>
                  </div>
                ))}
                {/* Additional dummy features for depth */}
                <div className="flex items-center gap-3 glass-effect p-3 rounded-xl border border-white/5">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 text-emerald-400 shrink-0"><path fillRule="evenodd" d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z" clipRule="evenodd" /></svg>
                  <span className="text-sm text-slate-300">Бесплатный Wi-Fi</span>
                </div>
                <div className="flex items-center gap-3 glass-effect p-3 rounded-xl border border-white/5">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 text-emerald-400 shrink-0"><path fillRule="evenodd" d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z" clipRule="evenodd" /></svg>
                  <span className="text-sm text-slate-300">Рум-сервис 24/7</span>
                </div>
              </div>
            </div>

            {/* Sticky Footer Button */}
            <div className="p-6 border-t border-white/10 bg-slate-900 sticky bottom-0 z-20">
              <button className="w-full bg-emerald-500 hover:bg-emerald-400 text-white font-bold py-4 rounded-full transition-colors shadow-[0_0_20px_rgba(16,185,129,0.3)]">
                Забронировать номер
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
